import { Dispatch } from 'redux';
import { axiosInstance, request, requestConfig } from './base';
import {
  CadastroParticipanteRequest,
  ParticipanteResponse,
  ParticipanteDocumentResponse,
  RegistroStatus,
  HistoricoResponse,
  PaginatedResult,
  POSTParticipanteFilterParams,
  GETParticipanteFilterParams,
} from 'common/genericTypes';
import { errorMessages } from 'common/errorMessages.strings';

export const POSTParticipante = async (
  dispatch: Dispatch,
  participante: CadastroParticipanteRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.post<ParticipanteResponse>(`${requestConfig.api}api/v1/participantes`, participante),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTParticipante = async (
  dispatch: Dispatch,
  participanteId: string,
  participante: CadastroParticipanteRequest,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.put<ParticipanteResponse>(
        `${requestConfig.api}api/v1/participantes/${participanteId}`,
        participante
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const PUTUploadDoc = async (
  dispatch: Dispatch,
  participanteId: string,
  participanteDocumentType: string,
  file: File,
  dataVencimento: string | null = null,
  stackRequest: boolean = false,
) => {
  const data = new FormData();
  data.append('file', file);
  return await request(
    () =>
      axiosInstance.put<ParticipanteDocumentResponse>(
        `${requestConfig.api}api/v1/participantes/${participanteId}/document`,
        data,
        {
          params: {
            participanteDocumentType,
            dataVencimento
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETParticipante = async (dispatch: Dispatch, participanteId: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.get<ParticipanteResponse>(`${requestConfig.api}api/v1/participantes/${participanteId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETParticipantes = async (
  dispatch: Dispatch,
  params?: GETParticipanteFilterParams,
  stackRequest: boolean = false
): Promise<ParticipanteResponse[]> => {
  let result = await request<Promise<ParticipanteResponse[]>>(
    () => axiosInstance.get(`${requestConfig.api}api/v1/participantes`, params ? { params } : undefined),
    dispatch,
    undefined,
    stackRequest
  );

  return result;
};

export const GETParticipantesResume = async (
  dispatch: Dispatch,
  params?: GETParticipanteFilterParams,
  stackRequest: boolean = false
): Promise<ParticipanteResponse[]> => {
  let result = await request<Promise<ParticipanteResponse[]>>(
    () => axiosInstance.get(`${requestConfig.api}api/v1/participantes/resume`, params ? { params } : undefined),
    dispatch,
    undefined,
    stackRequest
  );

  return result;
};

export const GETParticipantesPaginated = async (
  dispatch: Dispatch,
  params?: GETParticipanteFilterParams,
  stackRequest: boolean = false
): Promise<PaginatedResult<ParticipanteResponse>> => {
  const result = await request(
    () =>
      axiosInstance.get<PaginatedResult<ParticipanteResponse>>(
        `${requestConfig.api}api/v1/participantes/paginated`,
        params ? { params } : undefined
      ),
    dispatch,
    undefined,
    stackRequest
  );

  return result;
};

export const GETParticipantesSolicitadosPeloUsuario = async (
  dispatch: Dispatch,
  stackRequest?: boolean
): Promise<Array<ParticipanteResponse>> => {
  const result = await request(
    () =>
      axiosInstance.get<Array<ParticipanteResponse>>(
        `${requestConfig.api}api/v1/participantes/solicitados`
      ),
    dispatch,
    undefined,
    stackRequest
  );
  return result;
};

export const POSTParticipantesPaginated = async (
  dispatch: Dispatch,
  filter?: POSTParticipanteFilterParams,
  stackRequest: boolean = false
): Promise<PaginatedResult<ParticipanteResponse>> => {
  return await request(
    () =>
      axiosInstance.post<PaginatedResult<ParticipanteResponse>>(
        `${requestConfig.api}api/v1/participantes/paginated`,
        filter
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETHasCnpj = async (dispatch: Dispatch, cnpj: string, stackRequest: boolean = false) => {
  return await request(
    () => axiosInstance.get<boolean>(`${requestConfig.api}api/v1/participantes/cnpj/${cnpj}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTValidateParticipante = async (
  dispatch: Dispatch,
  status: RegistroStatus,
  participanteId: string,
  justificativa?: string,
  attachments?: File[],
  stackRequest: boolean = false
) => {
  const formData = new FormData();
  attachments?.forEach((attachment) => {
    formData.append('anexos', attachment);
  });
  const anexos = attachments && formData;

  const validationEndpoints: { [key: string]: string } = {
    [RegistroStatus.Aprovado]: `${requestConfig.api}api/v1/participantes/${participanteId}/approve`,
    [RegistroStatus.AguardandoCorrecao]: `${requestConfig.api}api/v1/participantes/${participanteId}/reject-pendencies`,
    [RegistroStatus.Reprovado]: `${requestConfig.api}api/v1/participantes/${participanteId}/reject`,
  };

  const currentEndpoint = validationEndpoints[status];
  return await request(
    () =>
      axiosInstance.post(currentEndpoint, anexos, {
        params: {
          justificativa,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    dispatch,
    errorMessages.validation.participante.generic(status),
    stackRequest
  );
};

export const GETDocumentDownloadUrl = async (
  dispatch: Dispatch,
  participanteId: string,
  documentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<string>(
        `${requestConfig.api}api/v1/participantes/${participanteId}/document/${documentId}/download`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const DELETEUploadDoc = async (
  dispatch: Dispatch,
  participanteId: string,
  documentId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () => axiosInstance.delete(`${requestConfig.api}api/v1/participantes/${participanteId}/document/${documentId}`),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTSuspenderParticipante = async (
  dispatch: Dispatch,
  participanteId: string,
  justificativa: string,
  anexosList: File[],
  stackRequest: boolean = false
) => {
  const formData = new FormData();
  anexosList.forEach((anexo) => {
    formData.append('anexos', anexo);
  });

  const anexos = anexosList && formData;

  return await request(
    () =>
      axiosInstance.post<ParticipanteResponse>(
        `${requestConfig.api}api/v1/participantes/${participanteId}/suspend`,
        anexos,
        {
          params: {
            justificativa,
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const POSTLiberarParticipante = async (
  dispatch: Dispatch,
  participanteId: string,
  justificativa: string,
  anexosList: File[],
  stackRequest: boolean = false
) => {
  const formData = new FormData();
  anexosList.forEach((anexo) => {
    formData.append('anexos', anexo);
  });

  const anexos = anexosList && formData;

  return await request(
    () =>
      axiosInstance.post<ParticipanteResponse>(
        `${requestConfig.api}api/v1/participantes/${participanteId}/unsuspend`,
        anexos,
        {
          params: {
            justificativa,
          },
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    dispatch,
    undefined,
    stackRequest
  );
};

export const GETHistoricoParticipante = async (
  dispatch: Dispatch,
  participanteId: string,
  stackRequest: boolean = false
) => {
  return await request(
    () =>
      axiosInstance.get<Array<HistoricoResponse>>(
        `${requestConfig.api}api/v1/participantes/${participanteId}/historico-de-alteracoes`
      ),
    dispatch,
    undefined,
    stackRequest
  );
};
